import type { DateTime } from "luxon";
import { type FC, type ReactNode, useCallback, useState } from "react";
import { useCompleteSelfScheduling, useImportSelfScheduling } from "../../hooks/useSelfScheduling";
import { useHospitalData } from "../HospitalDataProvider";
import { SelfSchedulingContext } from "./SelfSchedulingContext";

export const SelfSchedulingProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const { chosenDepartment, refreshDepartmentData } = useHospitalData();
	const { mutateAsync: importScheduling } = useImportSelfScheduling();
	const { mutateAsync: completeScheduling } = useCompleteSelfScheduling();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isImportingSelfScheduling, setIsImportingSelfScheduling] = useState<boolean>(!!chosenDepartment.selfScheduling?.locked);

	const toggleImportingSelfScheduling = useCallback(async (): Promise<void> => {
		try {
			setIsLoading(true);
			if (isImportingSelfScheduling) await completeScheduling();
			else await importScheduling();
			await refreshDepartmentData(chosenDepartment.department_id, true, chosenDepartment.selfScheduling?.periodStart);
			setIsImportingSelfScheduling((prev) => !prev);
		} finally {
			setIsLoading(false);
		}
	}, [
		importScheduling,
		completeScheduling,
		isImportingSelfScheduling,
		chosenDepartment.department_id,
		chosenDepartment.selfScheduling?.periodStart,
		refreshDepartmentData,
	]);

	const isDataInSelfSchedulingPeriod = useCallback(
		(date: DateTime): boolean =>
			!!chosenDepartment.selfScheduling &&
			isImportingSelfScheduling &&
			date >= chosenDepartment.selfScheduling.periodStart &&
			date <= chosenDepartment.selfScheduling.periodEnd,
		[chosenDepartment.selfScheduling, isImportingSelfScheduling],
	);

	return (
		<SelfSchedulingContext.Provider value={{ isImportingSelfScheduling, toggleImportingSelfScheduling, isDataInSelfSchedulingPeriod, isLoading }}>
			{children}
		</SelfSchedulingContext.Provider>
	);
};
